import React, { useEffect, useRef, useState } from 'react';
import {
  integrationChips,
  painPoints,
  services,
  steps,
  testimonials,
} from './landingData';
import { sendContactForm } from './contactService';

const logoIcons = {
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M11.999 1.5C6.201 1.5 1.5 6.201 1.5 12c0 1.861.484 3.61 1.332 5.13L1.5 22.5l5.499-1.314A10.452 10.452 0 0012 22.5c5.799 0 10.5-4.701 10.5-10.5S17.798 1.5 11.999 1.5z" fillOpacity=".4" />
    </svg>
  ),
  messenger: (
    <svg viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  email: (
    <svg viewBox="0 0 24 24" fill="#0078D4" aria-hidden="true">
      <path d="M21.5 2h-19A2.5 2.5 0 000 4.5v15A2.5 2.5 0 002.5 22h19a2.5 2.5 0 002.5-2.5v-15A2.5 2.5 0 0021.5 2zM3 5h18l-9 7L3 5zm0 14V7.5l9 7 9-7V19H3z" />
    </svg>
  ),
  wordpress: (
    <svg viewBox="0 0 24 24" fill="#21759B" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
    </svg>
  ),
};

function IconChip({ chip }) {
  return (
    <div className="logo-chip">
      {logoIcons[chip.svg]}
      {chip.label}
    </div>
  );
}

function App() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.animation = 'fadeUp 0.6s ease-out forwards';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    document.querySelectorAll('.service-card, .pain-card, .stat-card, .testimonial, .step').forEach((element) => {
      element.style.opacity = '0';
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries());

    setIsSubmitting(true);
    setFormError('');

    try {
      await sendContactForm(payload);
      form.reset();
      setIsSubmitted(true);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'No se pudo enviar el correo');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <nav>
        <a href="#top" className="nav-logo">
          Algoriza<span className="dot">.</span>
        </a>
        <div className="nav-links">
          <a href="#servicios">Servicios</a>
          <a href="#proceso">¿Cómo funciona?</a>
          <a href="#testimonios">Casos de éxito</a>
          <a href="#contacto" className="nav-cta">
            Hablar con un experto
          </a>
        </div>
      </nav>

      <main id="top">
        <section className="hero" ref={sectionRef}>
          <div className="hero-inner">
            <div className="hero-text fade-up">
              <div className="hero-badge">
                <span className="badge-dot" />
                Inteligencia Artificial para PyMes
              </div>
              <h1>
                IA que trabaja para tu <span>negocio</span>, sin necesidad de TI
              </h1>
              <p>
                Automatizamos las tareas repetitivas de tu empresa con soluciones de inteligencia artificial simples,
                rápidas y con retorno de inversión desde el primer mes.
              </p>
              <div className="hero-actions">
                <a href="#contacto" className="btn-primary">
                  Agenda una demo gratuita →
                </a>
                <a href="#servicios" className="btn-secondary">
                  Ver servicios
                </a>
              </div>
            </div>
            <div className="hero-visual fade-up delay-2">
              <div className="hero-card tall">
                <div className="hero-card-icon ic-teal">💬</div>
                <h4>Chatbot activo</h4>
                <div className="chat-container" style={{ marginTop: 12 }}>
                  <div className="chat-bubble">¿Cuál es su horario de atención?</div>
                  <div className="chat-bubble reply">Atendemos de lunes a sábado de 9am a 7pm 🕘</div>
                  <div className="chat-bubble">¿Hacen envíos a Monterrey?</div>
                  <div className="chat-bubble reply">¡Sí! Envío gratis a todo México en compras +$999</div>
                  <div className="typing-dots">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              </div>
              <div className="hero-card">
                <div className="stat-big">94%</div>
                <div className="stat-label">Consultas resueltas automáticamente</div>
              </div>
              <div className="hero-card">
                <div className="hero-card-icon ic-amber">⚡</div>
                <h4>Respuesta inmediata</h4>
                <p>24/7, sin costo de personal adicional</p>
              </div>
              <div className="hero-card">
                <div className="stat-big" style={{ fontSize: '1.9rem' }}>
                  -68%
                </div>
                <div className="stat-label">Reducción en tiempo de respuesta</div>
              </div>
            </div>
          </div>
        </section>

        <div className="logos">
          <div className="logos-inner">
            <span className="logos-label">Integrado con</span>
            {integrationChips.map((chip) => (
              <IconChip key={chip.label} chip={chip} />
            ))}
            <div className="logo-chip">🔗 Más integraciones</div>
          </div>
        </div>

        <section className="problema">
          <div className="section-inner">
            <div className="section-tag">¿Te identificas?</div>
            <h2 className="section-title" style={{ color: 'white' }}>
              Tu equipo pasa horas respondiendo
              <br />
              las mismas preguntas
            </h2>
            <p className="section-sub">
              Las PyMes pierden tiempo y dinero en tareas repetitivas que la IA puede resolver automáticamente, sin
              contratar más personal ni invertir en TI.
            </p>
            <div className="pain-grid" style={{ marginTop: '3rem' }}>
              {painPoints.map((pain) => (
                <div className="pain-card" key={pain.title}>
                  <div className="pain-icon">{pain.icon}</div>
                  <h4>{pain.title}</h4>
                  <p>{pain.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="servicios" id="servicios">
          <div className="section-inner">
            <div className="section-tag">Lo que hacemos</div>
            <h2 className="section-title">Soluciones de IA prácticas para tu empresa</h2>
            <p className="section-sub">
              Sin tecnicismos, sin departamento de TI. Implementamos y tú te dedicas a lo que importa: tu negocio.
            </p>
            <div className="services-grid">
              {services.map((service) => (
                <div className="service-card" key={service.title}>
                  <div className="service-icon-wrap">{service.icon}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <div className="service-tags">
                    {service.tags.map((tag) => (
                      <span className="tag" key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="proceso" id="proceso">
          <div className="section-inner">
            <div className="section-tag">Cómo trabajamos</div>
            <h2 className="section-title">De cero a automatizado en 4 semanas</h2>
            <p className="section-sub">
              Un proceso claro, sin sorpresas y con resultados visibles desde la primera semana de operación.
            </p>
            <div className="steps">
              {steps.map((step) => (
                <div className="step" key={step.num}>
                  <div className="step-num">{step.num}</div>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="stats">
          <div className="section-inner">
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <div className="section-tag">Resultados reales</div>
              <h2 className="section-title">Lo que logran nuestros clientes</h2>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="num">
                  <span>+80%</span>
                </div>
                <div className="desc">Reducción en tiempo destinado a consultas repetitivas</div>
              </div>
              <div className="stat-card">
                <div className="num">
                  <span>24</span>/7
                </div>
                <div className="desc">Horas de atención automática sin costo adicional de personal</div>
              </div>
              <div className="stat-card">
                <div className="num">
                  <span>4</span>sem
                </div>
                <div className="desc">Tiempo promedio de implementación completa</div>
              </div>
              <div className="stat-card">
                <div className="num">
                  <span>3x</span>
                </div>
                <div className="desc">Retorno de inversión promedio en los primeros 3 meses</div>
              </div>
            </div>
          </div>
        </section>

        <section className="testimonios" id="testimonios">
          <div className="section-inner">
            <div className="section-tag">Casos de éxito</div>
            <h2 className="section-title">PyMes que ya automatizan con Algoriza</h2>
            <div className="testimonios-grid">
              {testimonials.map((testimonial) => (
                <div className="testimonial" key={testimonial.author}>
                  <div className="stars">★★★★★</div>
                  <blockquote>“{testimonial.quote}”</blockquote>
                  <div className="testimonial-author">
                    <div className={`author-avatar ${testimonial.avatarClass}`}>{testimonial.initials}</div>
                    <div>
                      <div className="author-name">{testimonial.author}</div>
                      <div className="author-biz">{testimonial.business}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>
            ¿Listo para que la IA trabaje
            <br />
            <span>por ti?</span>
          </h2>
          <p>
            Agenda una llamada de 30 minutos sin costo. Te mostramos exactamente cómo podemos automatizar tu negocio y
            cuánto ahorrarás.
          </p>
          <div className="cta-actions">
            <a href="#contacto" className="btn-cta-primary">
              Agendar demo gratuita →
            </a>
            <a href="https://wa.me/5215549580630" className="btn-cta-ghost">
              💬 Escribir por WhatsApp
            </a>
          </div>
        </section>

        <section className="contacto" id="contacto">
          <div className="section-inner" style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div className="contacto-inner">
              <div className="contact-info">
                <div className="section-tag">Contáctanos</div>
                <h3>Platiquemos sobre tu negocio</h3>
                <p>
                  Sin compromiso. Nos cuentas cómo opera tu empresa y nosotros te decimos si hay una solución de IA que
                  te conviene — y cuánto te costaría.
                </p>
                <div className="contact-item">
                  <div className="ci-icon">📍</div>
                  <div className="ci-text">
                    <p>Ubicación</p>
                    <strong>Ciudad de México, México</strong>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="ci-icon">📧</div>
                  <div className="ci-text">
                    <p>Correo electrónico</p>
                    <strong>hola@algoriza.com.mx</strong>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="ci-icon">⚡</div>
                  <div className="ci-text">
                    <p>Tiempo de respuesta</p>
                    <strong>Menos de 2 horas en horario laboral</strong>
                  </div>
                </div>
                <a href="https://wa.me/5215549580630" className="whatsapp-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 1.5C6.201 1.5 1.5 6.201 1.5 12c0 1.861.484 3.61 1.332 5.13L1.5 22.5l5.499-1.314A10.452 10.452 0 0012 22.5c5.799 0 10.5-4.701 10.5-10.5S17.798 1.5 12 1.5z" opacity=".3" />
                  </svg>
                  Chatear por WhatsApp
                </a>
              </div>
              <div className="form-wrap">
                <h4
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: 'var(--navy)',
                    marginBottom: '1.5rem',
                  }}
                >
                  Cuéntanos sobre tu negocio
                </h4>
                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Nombre</label>
                      <input id="name" type="text" placeholder="Tu nombre" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Empresa</label>
                      <input id="company" type="text" placeholder="Nombre del negocio" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Correo electrónico</label>
                    <input id="email" type="email" placeholder="correo@tuempresa.com" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="whatsapp">WhatsApp (opcional)</label>
                    <input id="whatsapp" type="tel" placeholder="+52 55 0000 0000" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="service">¿Qué servicio te interesa?</label>
                    <select id="service" defaultValue="">
                      <option value="">Selecciona una opción</option>
                      <option>Chatbot y asistente virtual</option>
                      <option>Automatización de respuestas</option>
                      <option>Clasificación de tickets</option>
                      <option>Generación de contenido</option>
                      <option>Análisis de datos</option>
                      <option>Automatización de tareas internas</option>
                      <option>No sé, quiero asesoría</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="challenge">¿Cuál es tu principal reto hoy?</label>
                    <textarea
                      id="challenge"
                      placeholder="Cuéntanos brevemente qué proceso quisieras automatizar o mejorar…"
                    />
                  </div>
                  <button className="form-submit" type="submit" disabled={isSubmitting || isSubmitted}>
                    {isSubmitting
                      ? 'Enviando...'
                      : isSubmitted
                        ? '✅ Mensaje enviado — te contactamos pronto'
                        : 'Enviar y agendar demo gratuita →'}
                  </button>
                  {formError ? (
                    <p style={{ fontSize: '0.8rem', color: '#FF6B6B', marginTop: 10, textAlign: 'center' }}>
                      {formError}
                    </p>
                  ) : null}
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 10, textAlign: 'center' }}>
                    Sin costo. Sin compromiso. Respondemos en menos de 2 horas.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="footer-inner">
          <div className="footer-logo">
            Algoriza<span>.</span>com.mx
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href="#servicios">Servicios</a>
            <a href="#proceso">Cómo funciona</a>
            <a href="#testimonios">Casos de éxito</a>
            <a href="#contacto">Contacto</a>
          </div>
          <div>© 2026 Algoriza · Todos los derechos reservados</div>
        </div>
      </footer>
    </>
  );
}

export default App;
